import React, { useMemo, useEffect } from "react";
import styled, { css } from "styled-components";
import Tooltippy from "./Tooltippy";
import randomstring from "randomstring";
import CloseV2 from "./Icons/CloseV2";

const MAX_TOTAL_SIZE_MB = 25;
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

const FileUploadStyles = styled.div`
  ${({ ...props }) => css`
    padding: 8px 0 24px 0;
    label {
      cursor: pointer;
      strong {
        text-decoration: underline;
      }
    }
    input {
      border: 0 !important;
      clip: rect(1px, 1px, 1px, 1px) !important;
      -webkit-clip-path: inset(50%) !important;
      clip-path: inset(50%) !important;
      height: 1px !important;
      margin: -1px !important;
      overflow: hidden !important;
      padding: 0 !important;
      position: absolute !important;
      width: 1px !important;
      white-space: nowrap !important;
    }

    ul {
      margin-top: .5rem;
      padding-left: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    li {
      display: grid;
      grid-template-columns: auto max-content 24px;
      align-items: center;
      gap: 0.5rem;

      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 0.95rem;
      }

      button {
        all: unset;
        cursor: pointer;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 100%;
          height: 100%;

          path {
            fill: red;
            transition: fill .25s ease-out;
          }
        }

        &:hover svg path {
          fill: darkred;
        }
      }
    }

    .error {
      color: red;
      font-weight: bold;
      margin-top: .5rem;
    }
  `}
`;

export const FileInput = ({ files, setFiles, error, setError }) => {
  const rand = useMemo(() => randomstring.generate(5), []);

  // Watch total size
  useEffect(() => {
    const total = files.reduce((sum, f) => sum + f.size, 0);
    if (total > MAX_TOTAL_SIZE_BYTES) {
      setError(`Total file size exceeds ${MAX_TOTAL_SIZE_MB}MB. Please remove some files.`);
    } else {
      setError("");
    }
  }, [files, setError]);

  const handleFileUploadChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  return (
    <FileUploadStyles>
      <Tooltippy text="You can upload an unlimited number of files, but with a maximum combined size of 25mb per form submission.">
        <label htmlFor={`${rand}_file`}>
          <strong>Attach files</strong> (optional)
        </label>
      </Tooltippy>
      <input
        type="file"
        id={`${rand}_file`}
        multiple
        onChange={handleFileUploadChange}
      />
      {files.length > 0 && (
        <ul>
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`}>
              <span>{file.name}</span>
              <span>({(file.size / 1024 / 1024).toFixed(2)}MB)</span>
              <button onClick={() => removeFile(index)}><CloseV2 /></button>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="error">{error}</div>}
    </FileUploadStyles>
  );
};
